<template>
    <page :tabs="tabs" :busy="busy" :show-nav="!isMobile">
        <div v-if="isMobile" class="mt-1" />
        <grid
            ref="grid"
            :autoGroupColumnDef="autoGroupColumnDef"
            :column-defs="columnDefs"
            :row-data="rowData"
            :get-data-path="getDataPath"
            :tree-data="true"
            :is-auto-width="true"
            :is-search="false"
            :statusBar="statusBar"
            :getRowStyle="getRowStyle"
            :pinned-bottom-row-data="pinnedBottomRowData"
            grid-theme="ag-theme-alpine-report"
            :isGroupOpenByDefault="() => expanded"
        >
            <date-range-picker
                :rangeOptions="predefinedRanges"
                :value="dateRange"
                :dateFormat="userUnicodeDateFormat"
                :disableCustomRange="true"
                @input="handleDaterangeChange"
                class="mb-md-0 mb-1"
            />
            <div
                class="
                    d-none d-sm-flex
                    align-items-baseline
                    mr-1
                    mb-1
                    filter-properties
                "
            >
                <properties-search-multiselect
                    v-model="filterProperties"
                    variant="primary"
                    :properties="filterPropertyOptions"
                />
            </div>
            <export-to-excel-button
                :href="exportExcelHref"
            ></export-to-excel-button>
            <export-to-pdf-button
                :href="exportPdfHref"
                :pdfCacheKey="pdfCacheKey"
            />
            <responsive-button
                :icon="['fas', 'up-right-and-down-left-from-center']"
                variant="primary"
                @click="onButtonExpand"
            >
                <span v-if="expanded">Collapse</span>
                <span v-else>Expand</span>
            </responsive-button>
            <b-button-group class="mr-1">
                <more-filters-dropdown class="income-summary-dropdown">
                    <template
                        v-slot:default="{
                            handleDropdownShow,
                            handleDropdownHide,
                        }"
                    >
                        <properties-search-multiselect
                            class="d-sm-none d-flex"
                            v-model="filterProperties"
                            variant="primary"
                            :properties="filterPropertyOptions"
                            @show="handleDropdownShow"
                            @hide="handleDropdownHide"
                        />
                        <form-square-switch
                            class="mb-1 income-summary-toggle"
                            v-model="showRooms"
                            :labels="{
                                checked: 'Show Rooms',
                                unchecked: 'Show Rooms',
                            }"
                            :sync="true"
                        />
                        <form-square-switch
                            class="mb-1 income-summary-toggle"
                            v-model="showDisabledProperties"
                            :labels="{
                                checked: 'Show Disabled Properties',
                                unchecked: 'Show Disabled Properties',
                            }"
                            :sync="true"
                        />
                    </template>
                </more-filters-dropdown>
            </b-button-group>
            <responsive-button
                variant="outline-primary"
                @click="generate"
                :responsiveness="false"
                >Generate</responsive-button
            >
            <report-settings-modal
                :modalId="reportSettingsModalId"
                :initialValues="reportSettings"
            />
        </grid>
    </page>
</template>

<script>
import { mapGetters } from 'vuex';
import API from '../../api';
import Page from '../generic/page/page.vue';
import Grid from '../generic/grid/grid.vue';
import Modal from '../generic/modal/modal.vue';
import ResponsiveButton from '../generic/responsive-button/responsive-button.vue';
import ResponsiveDropdown from '../generic/responsive-dropdown/responsive-dropdown.vue';
import MoreFiltersDropdown from '../generic/more-filters-dropdown/more-filters-dropdown.vue';
import PropertiesSearchMultiselect from '../generic/properties-search-multiselect/properties-search-multiselect.vue';
import FormSquareSwitch from '../generic/form-square-switch/form-square-switch.vue';
import ExportToExcelButton from '../generic/export-to-excel-button/export-to-excel-button.vue';
import {
    REPORT_INCOME_SUMMARY_ENDPOINT,
    REPORT_INCOME_SUMMARY_PROPERTIES_ENDPOINT,
} from '../../endpoints';
import DateRangePicker, {
    nameToRange,
    LAST_YEAR,
    THIS_YEAR,
    NEXT_YEAR,
    LAST_N_YEARS_2,
    LAST_N_YEARS_3,
    LAST_N_YEARS_4,
    LAST_N_YEARS_5,
    LAST_N_YEARS_6,
    LAST_N_YEARS_7,
    LAST_N_YEARS_8,
    LAST_N_YEARS_9,
    LAST_N_YEARS_10,
} from '../generic/date-range-picker/date-range-picker.vue';
import ReportSummaryBar from './report-summary-bar.vue';
import { getYear } from 'date-fns';
import { removeScrollMixin } from '../../mixins/remove-scroll-mixin';
import { isMediumScreen } from '../../utils/screens';
import { toAmountDisplay } from '../../utils/numbers';
import ReportSettingsModal, {
    DATE_PERIODS_INPUT,
    SWITCH_INPUT,
    PROPERTIES_MULTI_SELECT,
} from './report-settings/report-settings-modal.vue';
import { defineAsyncComponent } from 'vue';

const exportToPdfButton = defineAsyncComponent(() =>
    import(
        /* webpackChunkName: "lx-export-to-pdf-button" */
        '../generic/export-to-pdf-button/export-to-pdf-button.vue'
    ))

export default {
    name: 'IncomeSummaryReport',
    components: {
        Page,
        Grid,
        Modal,
        DateRangePicker,
        ResponsiveButton,
        ResponsiveDropdown,
        reportSummaryBar: ReportSummaryBar,
        exportToPdfButton,
        ExportToExcelButton,
        FormSquareSwitch,
        MoreFiltersDropdown,
        PropertiesSearchMultiselect,
        ReportSettingsModal,
    },
    mixins: [removeScrollMixin],
    data() {
        const defaultYear =
            this.$store.getters['incomeSummaryLocalStorage/year'];
        const defaultShowRooms =
            this.$store.getters['incomeSummaryLocalStorage/showRooms'];
        const defaultShowDisabledProperties =
            this.$store.getters[
                'incomeSummaryLocalStorage/showDisabledProperties'
            ];
        return {
            title: 'Income Summary',
            tabs: [
                {
                    title: 'Income Summary',
                    route: '/app/reports/income-summary/',
                    active: true,
                },
            ],
            busy: false,
            autoGroupColumnDef: {
                headerName: 'Property/Room',
                cellStyle: ({ data, node }) => {
                    return node.rowPinned || data.property_name
                        ? { 'font-weight': 'bold' }
                        : null;
                },
                valueGetter: ({ data }) => data.property_name || data.name,
                width: isMediumScreen() ? undefined : 195,
                autoWidthExclude: this.isMobile,
                pinned: 'left',
            },
            getDataPath: (data) => data.id_hierarchy,
            dateRange: nameToRange(defaultYear),
            predefinedRanges: [
                LAST_YEAR,
                THIS_YEAR,
                NEXT_YEAR,
                LAST_N_YEARS_2,
                LAST_N_YEARS_3,
                LAST_N_YEARS_4,
                LAST_N_YEARS_5,
                LAST_N_YEARS_6,
                LAST_N_YEARS_7,
                LAST_N_YEARS_8,
                LAST_N_YEARS_9,
                LAST_N_YEARS_10,
            ],
            gridApi: null,
            rowData: [],
            periods: [],
            totals: [],
            expanded: true,
            columnDefs: [],
            filterPropertyOptions: [],
            filterPropertyOptionIds: new Set(),
            showRooms: defaultShowRooms,
            showDisabledProperties: defaultShowDisabledProperties,
            windowWidth: 0,
            windowHeight: 0,
            statusBar: null,
            pdfCacheKey: null,
            reportSettingsModalId: 'report-settings-modal',
        };
    },
    computed: {
        ...mapGetters('header', [
            'userUnicodeDateFormat',
            'userCurrencySymbol',
        ]),
        pinnedBottomRowData() {
            const [, ...rest] = this.totals;
            return rest;
        },
        propertiesRequest() {
            return this.filterProperties
                .map((property) => property.property_id)
                .join(',');
        },
        filterProperties: {
            get() {
                const defaultProperties =
                    this.$store.getters['incomeSummaryLocalStorage/properties'];
                return defaultProperties.filter((property) =>
                    this.filterPropertyOptionIds.has(property.property_id)
                );
            },
            set(properties) {
                this.$store.dispatch(
                    'incomeSummaryLocalStorage/changeProperties',
                    properties
                );
            },
        },
        exportExcelHref() {
            let url = '/app/reports/income-summary/row-data/excel/';
            url += `?year=${getYear(this.dateRange.from)}`;
            url += `&show_rooms=${this.showRooms}`;
            url += `&show_disabled_properties=${this.showDisabledProperties}`;
            url += `&properties=${this.propertiesRequest}`;
            return url;
        },
        exportPdfHref() {
            let url = '/app/reports/income-summary/row-data/pdf/';
            url += `?year=${getYear(this.dateRange.from)}`;
            url += `&show_rooms=${this.showRooms}`;
            url += `&show_disabled_properties=${this.showDisabledProperties}`;
            url += `&properties=${this.propertiesRequest}`;
            return url;
        },
        isMobile() {
            const md = 768;
            return window.innerWidth < md || window.innerHeight < md;
        },
        isThisYear() {
            return getYear(this.dateRange.from) === new Date().getFullYear();
        },
        reportSettings() {
            return {
                name: 'Income Summary',
                report_sysname: 'INCOME_SUMMARY',
                settings: [
                    {
                        type: DATE_PERIODS_INPUT,
                        value: 'this_year',
                        predefinedRanges: this.predefinedRanges,
                        label: 'Period',
                        name: 'year',
                    },
                    {
                        type: PROPERTIES_MULTI_SELECT,
                        value: [],
                        name: 'properties',
                        options: this.filterPropertyOptions,
                        label: 'Only For Properties',
                        placeholder: 'All Properties'
                    },
                    {
                        type: SWITCH_INPUT,
                        value: false,
                        label: 'Show Rooms',
                        name: 'show_rooms',
                    },
                    {
                        type: SWITCH_INPUT,
                        value: false,
                        label: 'Show Disabled Properties',
                        name: 'show_disabled_properties',
                    },
                ],
            };
        },
    },
    mounted() {
        this.fetchProperties();
        const grid = this.$refs.grid;
        this.gridApi = grid.gridOptions.api;
        this.onResize();
        window.addEventListener('resize', this.onResize);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.onResize);
    },
    beforeMount() {
        this.statusBar = {
            statusPanels: [
                {
                    key: 'reportSummaryBar',
                    statusPanel: 'reportSummaryBar',
                    align: 'left',
                },
            ],
        };
    },
    methods: {
        generate() {
            this.fetchData();
        },
        async fetchData() {
            try {
                this.busy = true;
                const { data } = await API.get(
                    REPORT_INCOME_SUMMARY_ENDPOINT(),
                    {
                        params: {
                            year: getYear(this.dateRange.from),
                            show_rooms: this.showRooms,
                            show_disabled_properties:
                                this.showDisabledProperties,
                            properties: this.propertiesRequest,
                        },
                    }
                );
                const {
                    periods,
                    properties,
                    property_data,
                    totals,
                    pdf_cache_key,
                } = data;
                this.pdfCacheKey = pdf_cache_key;
                this.periods = periods;
                this.rowData = properties;
                property_data.forEach((p) => {
                    this.rowData = this.rowData.concat(Object.values(p));
                });
                this.totals = Object.values(totals);
                this.columnDefs = [
                    ...periods.map((period) => {
                        return {
                            field: period.field_name,
                            headerName: period.header_name,
                            headerClass: 'ag-right-aligned-header',
                            cellClass: 'ag-right-aligned-cell',
                            cellRenderer: ({ data, value }) =>
                                this.rowCellRenderer(data.name, value),
                            pdfExportOptions: {
                                valueFormatter: ({ data, value }) =>
                                    this.rowCellRenderer(data.name, value),
                            },
                        };
                    }),
                    {
                        field: '',
                        headerName: '',
                        autoWidthExclude: true,
                        pdfExportOptions: {
                            skipColumn: true,
                        },
                        sortable: false,
                    },
                ];
                setTimeout(() => this.expandLevel0());
            } catch (e) {
                this.errorToast({ text: 'Could not fetch data' });
            } finally {
                this.busy = false;
            }
        },
        async fetchProperties() {
            try {
                const { data } = await API.get(
                    REPORT_INCOME_SUMMARY_PROPERTIES_ENDPOINT()
                );
                this.filterPropertyOptions = data;
                this.filterPropertyOptionIds = new Set(
                    data.map((option) => option.property_id)
                );
            } catch (e) {
                this.errorToast({ text: 'Could not fetch properties' });
            }
        },
        rowCellRenderer(row_name, value) {
            if (row_name === 'Occupancy') {
                return toAmountDisplay({
                    amount: value,
                    percentage: true,
                });
            } else if (row_name === 'Days Rented') {
                return value;
            } else {
                return toAmountDisplay({
                    amount: value,
                    currency: this.userCurrencySymbol,
                });
            }
        },
        onResize() {
            this.windowWidth = window.innerWidth;
            this.windowHeight = window.innerHeight;
            const statusBarComponent =
                this.gridApi.getStatusPanel('reportSummaryBar');
            let componentInstance = statusBarComponent;
            if (statusBarComponent.getFrameworkComponentInstance) {
                componentInstance =
                    statusBarComponent.getFrameworkComponentInstance();
            }
            componentInstance.setVisible(this.isMobile);
            componentInstance.setTitle(this.title);
            componentInstance.setCaption(
                'Invoices w/ depart dates outside of reporting period will be prorated by night.'
            );
        },
        expandLevel0() {
            this.gridApi.collapseAll();
            this.gridApi.forEachNode((node) => {
                if (node.level === 1) {
                    node.setExpanded(true);
                }
            });
            this.expanded = true;
        },
        onButtonExpand() {
            if (this.expanded) {
                this.gridApi.collapseAll();
                this.expanded = false;
            } else {
                this.expandLevel0();
            }
        },
        getRowStyle({ data, node }) {
            if (node.rowPinned) {
                return { 'font-weight': 'bold' };
            }
            if (data.property_name) {
                node.pdfRowStyle = { bold: true };
                if (node.rowIndex !== 0) {
                    node.pdfRowStyle.margin = [0, 15, 0, 0];
                }
            }
        },
        handleDaterangeChange(value) {
            this.dateRange = { ...value };
            this.$store.dispatch(
                'incomeSummaryLocalStorage/changeYear',
                value.name
            );
        },
    },
    watch: {
        showRooms(value) {
            this.$store.dispatch(
                'incomeSummaryLocalStorage/changeShowRooms',
                value
            );
        },
        showDisabledProperties(value) {
            this.$store.dispatch(
                'incomeSummaryLocalStorage/changeShowDisabledProperties',
                value
            );
        },
    },
};
</script>

<style lang="scss">
@import '~bootstrap/scss/_functions.scss';
@import '~bootstrap/scss/_variables.scss';
@import '~bootstrap/scss/mixins/_breakpoints.scss';

.income-summary-dropdown {
    :deep(.buttons-wrapper) {
        width: 100%;
    }

    .income-summary-toggle {
        width: 201px;
        @include media-breakpoint-down(lg) {
            width: 228px;
        }
    }
}
</style>
